import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom/cjs/react-router-dom'
import { useEffect, useState } from 'react'
import { toggleToContinueStatus } from '../../redux/user'
import { setEvaluations, setInformInfoInEvaluations } from '../../redux/evaluations'

import { FileText, CirclePlusIcon, FilesIcon, CameraIcon, Building2, UserCog, Stethoscope, ShieldCheck } from 'lucide-react'

import { LoadingScreen } from '../LoadingScreen'
import { USER_STATUS } from '../../const'

import { useGetInform } from '../../hooks/Login/useGetInform'
import { useCompleteEvaluationData } from '../../hooks/useCompleteEvaluationData'
import { NoStudyModal } from '../Modal/CustomModals/NoStudyModal'
import { useModal } from '../../hooks/useModal'
import { useUpdateUserEvaluations } from '../../hooks/useUpdateUserEvaluations'
import { useGetInformFromStudy } from '../../hooks/Inform/useGetInformFromStudy'
import { useAdminAPI } from '../../hooks/Admin/useAdminAPI'
import OphthalmologistPendingCard from './OphthalmologistPendingCard'

import {
  Card,
  CardContent,
  IconWrapper,
  Title,
  Description,
  ActionButton
} from './styles'

const TECHNICAL_CONTINUABLE_STATES = [
  'incompleto',
  'diagnosticando',
  'diagnosticado referible',
  'diagnosticado no referible'
]

const TECHNICAL_STATE_PRIORITY = {
  diagnosticando: 0,
  'diagnosticado referible': 1,
  'diagnosticado no referible': 1,
  incompleto: 2
}

const UserOptionCard = ({ icon, title, description, buttonText, path, onClick, loading = false }) => {
  const history = useHistory()

  const handleClick = () => {
    if (onClick) {
      onClick()
    } else if (path) {
      history.push(path)
    }
  }

  return (
    <Card>
      <CardContent>
        <IconWrapper>{icon}</IconWrapper>
        <Title>{title}</Title>
        <Description>{description}</Description>
        <ActionButton onClick={handleClick} disabled={loading}>
          {loading ? 'Cargando...' : buttonText}
        </ActionButton>
      </CardContent>
    </Card>
  )
}

export function UserOption ({ type, highlighted, children }) {
  const { data, action, getInform } = useGetInform()
  const { updateUserEvaluations } = useUpdateUserEvaluations()
  const { inform, getInformFromStudy } = useGetInformFromStudy()
  const { fetchEntities } = useAdminAPI()
  const [loading, setLoading] = useState(false)
  const [dataLoaded, setDataLoaded] = useState(false)
  const [attemptCount, setAttemptCount] = useState(0)
  const [pendingStudiesCount, setPendingStudiesCount] = useState(0)
  const [pendingStudiesLoading, setPendingStudiesLoading] = useState(false)
  const [modalRef, openModal, closeModal] = useModal()
  const history = useHistory()
  const { completeEvaluationData } = useCompleteEvaluationData()
  const { user } = useSelector(state => state)
  const dispatch = useDispatch()

  useEffect(() => {
    const load = async () => {
      const refreshedEvaluations = await updateUserEvaluations()
      if (user.role === 'technical') {
        const technicalEvaluations = Array.isArray(refreshedEvaluations)
          ? refreshedEvaluations
          : (Array.isArray(user && user.evaluations) ? user.evaluations : [])

        const currentEvaluation = technicalEvaluations
          .filter(evaluation => TECHNICAL_CONTINUABLE_STATES.includes(evaluation.estado))
          .sort((firstEvaluation, secondEvaluation) => {
            const firstPriority = TECHNICAL_STATE_PRIORITY[firstEvaluation.estado] ?? 99
            const secondPriority = TECHNICAL_STATE_PRIORITY[secondEvaluation.estado] ?? 99

            if (firstPriority !== secondPriority) {
              return firstPriority - secondPriority
            }

            return (secondEvaluation.id || 0) - (firstEvaluation.id || 0)
          })[0]

        if (currentEvaluation) {
          dispatch(toggleToContinueStatus())
          dispatch(setEvaluations(currentEvaluation))
        }
      }
      if (user.role === 'ophthalmologist') {
        const ophthalmologistEvaluations = Array.isArray(refreshedEvaluations)
          ? refreshedEvaluations
          : (Array.isArray(user && user.evaluations) ? user.evaluations : [])

        ophthalmologistEvaluations.forEach(evaluation => {
          if (evaluation.estado === 'informando') {
            dispatch(toggleToContinueStatus())
            if (evaluation.informes && evaluation.informes.length > 0) {
              const lastInforme = evaluation.informes[evaluation.informes.length - 1]
              if (lastInforme && lastInforme.id) {
                getInformFromStudy(lastInforme.id)
              }
            }
            dispatch(setEvaluations(evaluation))
          }
        })
      }
    }
    load()
  }, [])

  const getLastEvaluation = (evaluations) => {
    if (!Array.isArray(evaluations) || evaluations.length === 0) {
      return null
    }
    let last = evaluations[0]
    let lastId = 0
    for (let i = 0; i < evaluations.length; i++) {
      if (evaluations[i].estado === 'informado') {
        if (lastId < evaluations[i].id) {
          lastId = evaluations[i].id
          last = evaluations[i]
        }
      }
    }
    return last
  }

  const handleSetEvaluation = async () => {
    // si es el técnico, este botón permite dar de alta un nuevo estudio
    if (user.role === 'technical') {
      // acá no tiene sentido que se muestre un loading
      setLoading(false)
      // se redirige a la página de nuevo estudio
      history.push('/new-case')
    }

    // si es el paciente, este botón permite ver el último estudio realizado
    if (user.role === 'patient') {
      const fetchData = async () => {
        try {
          // se muestra el spinner de carga
          setLoading(true)
          // incrementar contador para forzar re-evaluación del useEffect
          setAttemptCount(prev => prev + 1)
          // se intenta levantar el último estudio realizado
          const evaluations = Array.isArray(user.evaluations) ? user.evaluations : []
          await getInform(user.token, 'last', getLastEvaluation(evaluations))
        } catch (error) {
          // si no hay un estudio realizado, se apaga el spinner de carga y se muestra el modal
          setLoading(false)
        }
      }
      await fetchData()
    }

    // si es el oftalmólogo en modo "continuar", este botón levanta su informe incompleto
    if (user.role === 'ophthalmologist') {
      const fetchData = async () => {
        try {
          // se muestra el spinner de carga
          setLoading(true)
          // incrementar contador para forzar re-evaluación del useEffect
          setAttemptCount(prev => prev + 1)
          // levanta el estudio asociado al informe incompleto del oftalmólogo
          await getInform(user.token, 'pending')
        } catch (error) {
          // si no hay estudios pendientes de informar, se desactiva el proceso de carga y se muestra el modal
          setLoading(false)
          openModal()
        }
      }
      await fetchData()
    }
  }

  const goToNewReport = () => {
    setLoading(false)
    history.push('/new-report')
  }

  const goToPatientReport = () => {
    setLoading(false)
    history.push('/new-report?mode=readonly')
  }

  useEffect(() => {
    if (dataLoaded) {
      if (user.role === 'patient') {
        goToPatientReport()
      }
      if (user.role === 'ophthalmologist') {
        goToNewReport()
      }
    }
  }, [dataLoaded])

  useEffect(() => {
    const fetchData = async () => {
      if (!dataLoaded) {
        if (data === false || data === 'no-data') {
          setLoading(false)
          openModal()
          setDataLoaded(false)
        } else if (data) {
          dispatch(setEvaluations(data))
          if (user.role === 'ophthalmologist') {
            dispatch(toggleToContinueStatus())
            await completeEvaluationData(data, action)
            setDataLoaded(true)
          }
          if (user.role === 'patient') {
            await completeEvaluationData(data, 'patient-readonly')
            setDataLoaded(true)
          }
        }
      }
    }
    fetchData()
  }, [data, attemptCount])

  useEffect(() => {
    if (type !== 'case' || user.role !== 'ophthalmologist' || user.status === USER_STATUS.CONTINUE) {
      return
    }

    let active = true

    const loadPendingStudiesCount = async () => {
      try {
        setPendingStudiesLoading(true)
        const studies = await fetchEntities('estudios/pendientes')
        if (!active) return
        setPendingStudiesCount(Array.isArray(studies) ? studies.length : 0)
      } catch (error) {
        if (!active) return
        setPendingStudiesCount(0)
      } finally {
        if (active) {
          setPendingStudiesLoading(false)
        }
      }
    }

    loadPendingStudiesCount()

    return () => {
      active = false
    }
  }, [fetchEntities, type, user.role, user.status])

  const shouldShowOphthalmologistLoading = (
    user.role === 'ophthalmologist' &&
    user.status === USER_STATUS.CONTINUE &&
    loading &&
    data !== false &&
    data !== 'no-data'
  )

  if (type === 'evaluations') {
    if (user.role === 'patient') {
      return (
        <UserOptionCard
          icon={<FilesIcon className='w-5 h-5' />}
          title='Estudios previos'
          description='Ver estudios previos realizados'
          buttonText='Ver estudios'
          path='/evaluations'
        />
      )
    }

    if (user.role === 'ophthalmologist') {
      return (
        <UserOptionCard
          icon={<FilesIcon className='w-5 h-5' />}
          title='Mis estudios informados'
          description='Revisar todos los estudios que has informado históricamente'
          buttonText='Listado de estudios'
          path='/evaluations'
        />
      )
    }

    // técnico (y fallback defensivo)
    return (
      <UserOptionCard
        icon={<FilesIcon className='w-5 h-5' />}
        title='Estudios del nodo'
        description='Ver los estudios capturados en tu nodo'
        buttonText='Listado de estudios'
        path='/evaluations'
      />
    )
  }

  if (type === 'new-patient') {
    // Solo técnico debe ver esto, pero por si acaso:
    return (
      <UserOptionCard
        icon={<CirclePlusIcon className='w-5 h-5' />}
        title='Alta de paciente'
        description='Para agregar o editar datos de pacientes'
        buttonText='Cargar paciente'
        path='/new-patient'
      />
    )
  }

  // type === 'case'
  if (type === 'case') {
    if (user.role === 'technical') {
      const isContinuar = user.status === USER_STATUS.CONTINUE
      return (
        <UserOptionCard
          icon={<CameraIcon className='w-5 h-5' />}
          title={isContinuar ? 'Continuar estudio' : 'Nuevo estudio'}
          description={isContinuar ? 'Tenés un estudio en curso' : 'Para cargar nuevas imágenes'}
          buttonText={isContinuar ? 'Continuar estudio' : 'Iniciar estudio'}
          onClick={handleSetEvaluation}
        />
      )
    } else {
      return (
        <>
          {/* Solo el Modal fuera */}
          <NoStudyModal ref={modalRef} close={closeModal} />

          {/* Renderizado condicional POR ROL */}
          {user.role === 'ophthalmologist' && (() => {
            if (shouldShowOphthalmologistLoading) {
              return <LoadingScreen />
            }

            if (user.status === USER_STATUS.CONTINUE) {
              return (
                <UserOptionCard
                  icon={<FileText className='w-5 h-5' />}
                  title='Evaluar estudios'
                  description='Tenés un informe en curso para completar'
                  buttonText='Continuar informe'
                  onClick={handleSetEvaluation}
                />
              )
            }

            return (
              <OphthalmologistPendingCard
                pendingCount={pendingStudiesCount}
                loading={pendingStudiesLoading}
                path='/pending-reports'
              />
            )
          })()}

          {user.role === 'patient' && (
            loading && data !== false && data !== 'no-data'
              ? (
                <LoadingScreen />
                )
              : (
                <UserOptionCard
                  icon={<CameraIcon className='w-5 h-5' />}
                  title='Ver estudio'
                  description='Consultar tu último estudio realizado'
                  buttonText='Ver último estudio'
                  onClick={handleSetEvaluation}
                />
                )
          )}
        </>
      )
    }
  }

  // Admin options
  if (type === 'admin-institutions') {
    return (
      <UserOptionCard
        icon={<Building2 className='w-5 h-5' />}
        title='Administrar Instituciones'
        description='Alta, baja y modificación de instituciones y nodos de captura'
        buttonText='Administrar'
        path='/admin/institutions'
      />
    )
  }

  if (type === 'admin-nodes') {
    return (
      <UserOptionCard
        icon={<Building2 className='w-5 h-5' />}
        title='Administrar Nodos'
        description='Alta, baja y modificación de nodos de captura'
        buttonText='Administrar'
        path='/admin/nodes'
      />
    )
  }

  if (type === 'admin-technicians') {
    return (
      <UserOptionCard
        icon={<UserCog className='w-5 h-5' />}
        title='Administrar Técnicos'
        description='Alta, baja y modificación de usuarios técnicos para nodos de captura'
        buttonText='Administrar'
        path='/admin/technicians'
      />
    )
  }

  if (type === 'admin-ophthalmologists') {
    return (
      <UserOptionCard
        icon={<Stethoscope className='w-5 h-5' />}
        title='Administrar Oftalmólogos'
        description='Alta, baja y modificación de usuarios oftalmólogos para instituciones'
        buttonText='Administrar'
        path='/admin/ophthalmologists'
      />
    )
  }

  if (type === 'admin-admins') {
    return (
      <UserOptionCard
        icon={<ShieldCheck className='w-5 h-5' />}
        title='Administrar Administradores'
        description='Alta, baja y modificación de administradores del sistema'
        buttonText='Administrar'
        path='/admin/admins'
      />
    )
  }

  if (type === 'admin-institution-admins') {
    return (
      <UserOptionCard
        icon={<Building2 className='w-5 h-5' />}
        title='Admins. de Institución'
        description='Alta, baja y modificación de administradores institucionales'
        buttonText='Administrar'
        path='/admin/institution-admins'
      />
    )
  }
}

export default UserOption
